// ══════════════════════════════════════════════════════════
// LIVE SESSION — Tipos (snake_case)
//
// Tipos para el contrato de sesiones live del backend PHP.
// Todos los campos en snake_case porque vienen directo del backend.
//
// Endpoints:
//   POST   /api/v1/live/start
//   GET    /api/v1/live/sessions/{sessionId}
//   POST   /api/v1/live/sessions/{sessionId}/finish
//
// Envelope estándar del backend:
//   { success, message, data, errors, meta }
//
// La sesión SIEMPRE viene en data.session.
// ══════════════════════════════════════════════════════════

// ── ESTADOS DE SESIÓN LIVE ──

export type LiveSessionStatus =
  | 'preparing'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'ending'
  | 'completed'
  | 'failed';

// ── SESIÓN LIVE ──

export interface LiveSession {
  id: string;
  status: LiveSessionStatus;
  radio_id: string;
  station_id: string;
  dj_id: string | null;
  user_id: string | null;
  program_id: string | null;
  created_by: string | null;
  created_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  last_heartbeat: string | null;
  failure_reason: string | null;
  duration_seconds: number | null;
  disconnect_count: number;
}

// ── DATA ENVELOPES POR ENDPOINT ──

export interface StartLiveSessionData {
  session: LiveSession;
  polling: unknown;
  autodj: unknown;
  next_step: unknown;
}

export interface GetLiveSessionData {
  session: LiveSession;
}

export interface FinishLiveSessionData {
  session: LiveSession;
  finish: unknown;
  heartbeat: unknown;
  autodj_monitor: unknown;
  next_step: unknown;
}

// ── PAYLOAD PARA INICIAR ──

export interface StartLiveSessionPayload {
  station_id: string;
  radio_id: string;
  program_id?: string;
}

// ═══════════════ HELPERS ═══════════════

/**
 * Mapa de estado a label amigable.
 */
export const LIVE_SESSION_STATUS_LABELS: Record<LiveSessionStatus, string> = {
  preparing: 'Preparando',
  connecting: 'Conectando',
  live: 'En vivo',
  reconnecting: 'Reconectando',
  ending: 'Finalizando',
  completed: 'Completado',
  failed: 'Fallido',
};

/**
 * Configuración de colores para badges de sesión live (dot, bg, text).
 */
export function liveSessionStatusColor(
  status: LiveSessionStatus,
): { dot: string; bg: string; text: string } {
  switch (status) {
    case 'preparing':
      return { dot: 'bg-foreground-400', bg: 'bg-foreground-100', text: 'text-foreground-600' };
    case 'connecting':
      return { dot: 'bg-amber-400', bg: 'bg-amber-100', text: 'text-amber-700' };
    case 'live':
      return { dot: 'bg-emerald-500', bg: 'bg-emerald-100', text: 'text-emerald-700' };
    case 'reconnecting':
      return { dot: 'bg-amber-500', bg: 'bg-amber-100', text: 'text-amber-700' };
    case 'ending':
      return { dot: 'bg-foreground-400', bg: 'bg-foreground-100', text: 'text-foreground-600' };
    case 'completed':
      return { dot: 'bg-emerald-400', bg: 'bg-emerald-50', text: 'text-emerald-600' };
    case 'failed':
      return { dot: 'bg-red-500', bg: 'bg-red-100', text: 'text-red-700' };
    default:
      return { dot: 'bg-foreground-300', bg: 'bg-foreground-100', text: 'text-foreground-600' };
  }
}

/**
 * Indica si la sesión está activa (transmitiendo o en transición hacia/desde live).
 */
export function isLiveSessionActive(status: LiveSessionStatus): boolean {
  return ['preparing', 'connecting', 'live', 'reconnecting', 'ending'].includes(status);
}

/**
 * Indica si la sesión ya terminó (bien o mal).
 */
export function isLiveSessionTerminal(status: LiveSessionStatus): boolean {
  return ['completed', 'failed'].includes(status);
}